import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { format } from "date-fns";
import { Router, type RouterLocation } from "@vaadin/router";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/button-group/button-group.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/textarea/textarea.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";
import "../components/app-shell";
import { getDb, saveEntry, deleteEntry } from "../db";
import type {
  LogEntry,
  PeriodTag,
  UserSettings,
  ExerciseType,
  ExerciseIntensity,
  SickStatus,
  ExerciseData,
  SickDayData,
} from "../types";
import {
  PERIOD_TAG_INFO,
  formatInsulinDose,
  getQATotal,
  calculateIOB,
  DEFAULT_SETTINGS,
  EXERCISE_TYPE_INFO,
  EXERCISE_INTENSITY_INFO,
  getCurrentSickStatus,
  getSickDayRecommendation,
  formatNextCheckTime,
  getNextCheckTime,
  getExerciseRecommendation,
  getRecentExercise,
  getKetoneAlert,
  getBgKetoneAlert,
} from "../types";
import {
  getMissingSettings,
  calculateQADoseForCarbs,
  getUnitsPerCPFromSettings,
  IS_BETA,
  BETA_WARNING,
} from "../config/dafne";

@customElement("add-entry-page")
export class AddEntryPage extends LitElement {
  @state() private currentDate = new Date();
  @state() private entries: LogEntry[] = [];
  @state() private settings: UserSettings = DEFAULT_SETTINGS;
  @state() private activeIOB = 0;
  @state() private currentSickStatus: SickDayData | null = null;
  @state() private settingsMissing: string[] = [];

  // Edit mode
  @state() private editId: string | null = null;
  @state() private editEntry: LogEntry | null = null;
  @state() private showDeleteConfirm = false;

  // Form fields
  @state() private formTime = "";
  @state() private formPeriod: PeriodTag = "B";
  @state() private formGL = "";
  @state() private formCP = "";
  @state() private formQABase = "";
  @state() private formQACorr = "";
  @state() private formBIBase = "";
  @state() private formBICorr = "";
  @state() private formKet = "";
  @state() private formNote = "";
  @state() private suggestedCorrection = 0;
  @state() private rawCorrection = 0; // Correction before IOB adjustment
  @state() private correctionBlockedByIOB = false; // True if no-stacking mode blocked correction
  @state() private suggestedQABase = 0;
  @state() private calculatedCP = 0; // Grams converted to CP rounded to 0.5

  // Exercise fields
  @state() private showExercise = false;
  @state() private formExerciseType: ExerciseType = "walk";
  @state() private formExerciseIntensity: ExerciseIntensity = "moderate";
  @state() private formExerciseDuration = "";

  // Sick day fields
  @state() private formSickStatus: SickStatus | null = null;

  static styles = css`
    :host {
      display: block;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #dee2e6;
      background: white;
    }

    .page-header h2 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      display: none;
    }

    /* Show title only on large screens where app-shell header is hidden */
    @media (min-width: 1024px) {
      .page-header h2 {
        display: block;
      }
    }

    .page-content {
      padding: 1rem;
      padding-bottom: 2rem;
    }

    .form-section {
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .form-row {
      margin-bottom: 1rem;
    }

    .form-row:last-child {
      margin-bottom: 0;
    }

    .form-row-inline {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    .form-row label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      margin-bottom: 0.25rem;
      color: #495057;
    }

    .form-row label small {
      font-weight: 400;
      color: #6c757d;
    }

    .correction-suggestion {
      background: #e7f1ff;
      border-radius: 0.5rem;
      padding: 0.75rem;
      margin-bottom: 1rem;
      font-size: 0.875rem;
    }

    .correction-suggestion .title {
      font-weight: 600;
      margin-bottom: 0.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .correction-suggestion .detail {
      color: #495057;
      font-size: 0.75rem;
    }

    .stacking-alert {
      margin-bottom: 1rem;
    }

    .form-actions {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding-top: 1rem;
      border-top: 1px solid #dee2e6;
      margin-top: 1rem;
    }

    /* Today's entries section */
    .entries-section {
      margin-top: 1.5rem;
    }

    .section-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .entry-card {
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
    }

    .entry-card.has-hypo {
      border-left: 3px solid #dc3545;
    }
    .entry-card.has-high-ket {
      border-left: 3px solid #dc3545;
    }

    .entry-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.25rem;
    }

    .entry-time {
      font-weight: 600;
      font-size: 0.875rem;
    }

    .entry-period {
      background: #e7f1ff;
      color: #0d6efd;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-size: 0.625rem;
      font-weight: 500;
    }

    .entry-data {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .entry-field {
      display: flex;
      flex-direction: column;
      min-width: 40px;
    }

    .entry-field .label {
      font-size: 0.5rem;
      color: #6c757d;
      text-transform: uppercase;
    }

    .entry-field .value {
      font-size: 0.875rem;
      font-weight: 500;
    }

    /* GL color coding */
    .gl-hypo {
      color: #dc3545;
      font-weight: 700;
    }
    .gl-low {
      color: #fd7e14;
    }
    .gl-target {
      color: #198754;
    }
    .gl-elevated {
      color: #fd7e14;
    }
    .gl-high {
      color: #dc3545;
    }

    /* Ketone color coding */
    .ket-normal {
      color: #198754;
    }
    .ket-elevated {
      color: #fd7e14;
    }
    .ket-high {
      color: #dc3545;
    }

    .empty-entries {
      color: #6c757d;
      font-size: 0.875rem;
      font-style: italic;
    }

    /* Status buttons (Sick/Exercise) */
    .status-buttons {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
      flex-wrap: wrap;
    }

    .status-btn {
      flex: 1;
      min-width: 120px;
    }

    .status-btn.active {
      --sl-color-primary-600: var(--btn-color, #0d6efd);
    }

    .sick-btn {
      --btn-color: #dc3545;
    }
    .better-btn {
      --btn-color: #198754;
    }
    .exercise-btn {
      --btn-color: #0d6efd;
    }

    /* Recommendations panel */
    .recommendations {
      background: #fff3cd;
      border: 1px solid #ffc107;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .recommendations.sick {
      background: #f8d7da;
      border-color: #dc3545;
    }

    .recommendations.exercise {
      background: #cfe2ff;
      border-color: #0d6efd;
    }

    .recommendations.ketone {
      background: #fff3cd;
      border-color: #ffc107;
    }

    .recommendations.ketone-high {
      background: #f8d7da;
      border-color: #dc3545;
    }

    .recommendations.ketone-danger {
      background: #dc3545;
      border-color: #721c24;
      color: white;
    }

    .recommendations.ketone-danger .rec-header,
    .recommendations.ketone-danger .rec-item {
      color: white;
    }

    .recommendations.ketone-danger .warning {
      color: #fff3cd;
    }

    .recommendations.bg-warning {
      background: #fff3cd;
      border-color: #ffc107;
    }

    .recommendations .rec-header {
      font-weight: 600;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .recommendations .rec-item {
      font-size: 0.875rem;
      padding: 0.25rem 0;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .recommendations .rec-item .icon {
      flex-shrink: 0;
    }

    .recommendations .warning {
      color: #dc3545;
      font-weight: 500;
    }

    .recommendations .action {
      color: #495057;
    }

    .recommendations .next-check {
      background: white;
      border-radius: 0.25rem;
      padding: 0.5rem;
      margin-top: 0.5rem;
      font-size: 0.875rem;
    }

    .recommendations .next-check strong {
      color: #0d6efd;
    }

    /* Exercise form section */
    .exercise-section {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .exercise-section .section-label {
      font-weight: 600;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .exercise-types {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
    }

    .exercise-type-btn {
      min-width: 70px;
    }

    .intensity-row {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    await this._loadSettings();
    await this._loadEntries();

    // Only set defaults if not in edit mode
    if (!this.editId) {
      this._setDefaultTime();
    }
    await this._calculateActiveIOB();
    this._checkSickStatus();
  }

  // Router lifecycle hook for edit mode
  onAfterEnter(location: RouterLocation) {
    const id = location.params.id as string | undefined;
    if (id) {
      this.editId = id;
      this._loadEntryForEdit(id);
    } else {
      this.editId = null;
      this.editEntry = null;
    }
  }

  private async _loadEntryForEdit(id: string) {
    try {
      const entry = await getDb().entries.get(id);
      if (entry) {
        this.editEntry = entry;
        // Populate form fields
        this.currentDate = new Date(entry.timestamp);
        this.formTime = format(new Date(entry.timestamp), "HH:mm");
        this.formPeriod = entry.periodTag;
        this.formGL = entry.bloodGlucose?.toString() || "";
        this.formCP = entry.carbs?.toString() || "";
        this.formQABase = entry.qaBase?.toString() || "";
        this.formQACorr = entry.qaCorrection?.toString() || "";
        this.formBIBase = entry.biBase?.toString() || "";
        this.formBICorr = entry.biCorrection?.toString() || "";
        this.formKet = entry.ketones?.toString() || "";
        this.formNote = entry.notes || "";

        // Exercise
        if (entry.exercise) {
          this.showExercise = true;
          this.formExerciseType = entry.exercise.type;
          this.formExerciseIntensity = entry.exercise.intensity;
          this.formExerciseDuration = entry.exercise.duration.toString();
        }

        // Sick day
        if (entry.sickDay) {
          this.formSickStatus = entry.sickDay.status;
        }
      }
    } catch (error) {
      console.error("Failed to load entry for edit:", error);
    }
  }

  private async _loadSettings() {
    try {
      const stored = await getDb().settings.get("settings");
      if (stored) this.settings = stored;
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  }

  private async _loadEntries() {
    const now = new Date();
    // Load past 24 hours of entries for IOB and other calculations
    const past24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    try {
      this.entries = await getDb()
        .entries.where("timestamp")
        .between(past24Hours, now)
        .sortBy("timestamp");
    } catch (error) {
      console.error("Failed to load entries:", error);
    }
  }

  private _checkSickStatus() {
    this.currentSickStatus = getCurrentSickStatus(this.entries);
  }

  private _setDefaultTime() {
    const now = new Date();
    this.formTime = format(now, "HH:mm");
    const hour = now.getHours();
    if (hour >= 5 && hour < 10) this.formPeriod = "B";
    else if (hour >= 10 && hour < 15) this.formPeriod = "L";
    else if (hour >= 15 && hour < 21) this.formPeriod = "EM";
    else if (hour >= 21) this.formPeriod = "BT";
    else this.formPeriod = "3AM";
  }

  /**
   * Build timestamp from currentDate and formTime
   */
  private _getEntryTimestamp(): Date {
    const [hours, minutes] = this.formTime.split(":").map(Number);
    const timestamp = new Date(this.currentDate);
    timestamp.setHours(hours || 0, minutes || 0, 0, 0);
    return timestamp;
  }

  /**
   * Handle time input change - recalculate IOB for new entry time
   */
  private async _onTimeChange(value: string) {
    this.formTime = value;
    await this._calculateActiveIOB();
    this._calculateSuggestions();
  }

  private async _calculateActiveIOB() {
    // Use the entry time being created, not current time
    const entryTime = this._getEntryTimestamp();
    // Look back 4 hours for IOB calculation (across day boundaries)
    const iobWindowStart = new Date(entryTime.getTime() - 4 * 60 * 60 * 1000);

    try {
      const recentEntries = await getDb()
        .entries.where("timestamp")
        .between(iobWindowStart, entryTime)
        .toArray();

      let totalIOB = 0;
      for (const entry of recentEntries) {
        const qaTotal = getQATotal(entry);
        if (qaTotal) {
          const hoursElapsed =
            (entryTime.getTime() - entry.timestamp.getTime()) /
            (1000 * 60 * 60);
          totalIOB += calculateIOB(qaTotal, hoursElapsed);
        }
      }
      this.activeIOB = Math.round(totalIOB * 10) / 10;
    } catch (error) {
      console.error("Failed to calculate IOB:", error);
      this.activeIOB = 0;
    }
  }

  private _calculateSuggestions() {
    const increment = this.settings.doseIncrement || 0.5;
    const stackingMode = this.settings.correctionStacking || "no-stacking";

    // Check if settings are configured
    this.settingsMissing = getMissingSettings(this.settings);

    // Calculate correction based on GL
    const gl = parseFloat(this.formGL);
    this.correctionBlockedByIOB = false;
    this.rawCorrection = 0;

    if (gl && this.settings.correctionFactor) {
      const target =
        (this.settings.targetBgLow + this.settings.targetBgHigh) / 2;
      const rawCorrectionCalc = (gl - target) / this.settings.correctionFactor;
      // Round raw correction to dose increment for display
      this.rawCorrection =
        Math.round(rawCorrectionCalc / increment) * increment;

      if (stackingMode === "no-stacking") {
        // DAFNE recommendation: Don't suggest corrections while IOB is active
        if (this.activeIOB > 0 && this.rawCorrection > 0) {
          this.correctionBlockedByIOB = true;
          this.suggestedCorrection = 0;
        } else {
          this.suggestedCorrection = this.rawCorrection;
        }
      } else {
        // IOB-adjusted mode: Subtract IOB from raw correction
        const adjustedCorrection = rawCorrectionCalc - this.activeIOB;
        this.suggestedCorrection =
          Math.round(adjustedCorrection / increment) * increment;
      }
    } else {
      this.suggestedCorrection = 0;
    }

    // Calculate QA base based on carbs and period ratio (DAFNE style)
    const grams = parseFloat(this.formCP);
    if (grams && grams > 0) {
      // Convert grams to DAFNE CP (1 CP = 10g), rounded to 0.5 increments
      this.calculatedCP = Math.round((grams / 10) * 2) / 2;

      const ratioKey =
        PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom || this.formPeriod;
      // Use centralized calculation from config/dafne/insulin.ts
      this.suggestedQABase = calculateQADoseForCarbs(
        this.calculatedCP,
        this.settings.cpRatios,
        ratioKey,
        increment,
      );
    } else {
      this.calculatedCP = 0;
      this.suggestedQABase = 0;
    }
  }

  private _onGLChange(value: string) {
    this.formGL = value;
    this._calculateSuggestions();
  }

  private _onCPChange(value: string) {
    this.formCP = value;
    this._calculateSuggestions();
  }

  private _onPeriodChange(value: PeriodTag) {
    this.formPeriod = value;
    this._calculateSuggestions();
  }

  private _applySuggestions() {
    if (this.suggestedQABase > 0) {
      this.formQABase = this.suggestedQABase.toString();
    }
    if (this.suggestedCorrection !== 0) {
      this.formQACorr = this.suggestedCorrection.toString();
    }
  }

  private _cancel() {
    Router.go("/");
  }

  // Use centralized conversion from config/dafne/insulin.ts
  private _getUnitsPerCP(period: string): number {
    return getUnitsPerCPFromSettings(this.settings.cpRatios, period);
  }

  private async _saveEntry() {
    const [hours, minutes] = this.formTime.split(":").map(Number);
    const timestamp = new Date(this.currentDate);
    timestamp.setHours(hours, minutes, 0, 0);

    // Build exercise data if provided
    let exerciseData: ExerciseData | undefined;
    if (this.showExercise && this.formExerciseDuration) {
      exerciseData = {
        type: this.formExerciseType,
        intensity: this.formExerciseIntensity,
        duration: parseInt(this.formExerciseDuration),
      };
    }

    // Build sick day data if provided
    let sickDayData: SickDayData | undefined;
    if (this.formSickStatus) {
      sickDayData = {
        status: this.formSickStatus,
        symptoms: this.formSickStatus === "sick" ? this.formNote : undefined,
      };
    }

    // Capture current ratios for historical review (store as units per CP)
    const ratioKey =
      PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom || this.formPeriod;
    const cpRatioUsed = this._getUnitsPerCP(ratioKey) || undefined;
    const correctionFactorUsed = this.settings.correctionFactor ?? undefined;

    const entry: LogEntry = {
      id: this.editId || crypto.randomUUID(),
      timestamp,
      periodTag: this.formPeriod,
      bloodGlucose: this.formGL ? parseFloat(this.formGL) : undefined,
      carbs: this.formCP ? parseFloat(this.formCP) : undefined,
      qaBase: this.formQABase ? parseFloat(this.formQABase) : undefined,
      qaCorrection: this.formQACorr ? parseFloat(this.formQACorr) : undefined,
      biBase: this.formBIBase ? parseFloat(this.formBIBase) : undefined,
      biCorrection: this.formBICorr ? parseFloat(this.formBICorr) : undefined,
      ketones: this.formKet ? parseFloat(this.formKet) : undefined,
      exercise: exerciseData,
      sickDay: sickDayData,
      notes: this.formNote || undefined,
      // Settings snapshot - capture ratios used at entry time
      cpRatioUsed,
      correctionFactorUsed,
      createdAt: this.editEntry?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    try {
      // Use saveEntry from operations.ts to ensure sync metadata is set
      await saveEntry(entry);
      Router.go("/");
    } catch (error) {
      console.error("Failed to save entry:", error);
    }
  }

  private async _deleteEntry() {
    if (!this.editId) return;

    try {
      // Use deleteEntry from operations.ts to ensure sync metadata is set (soft delete)
      await deleteEntry(this.editId);
      Router.go("/");
    } catch (error) {
      console.error("Failed to delete entry:", error);
    }
  }

  private _confirmDelete() {
    this.showDeleteConfirm = true;
  }

  private _cancelDelete() {
    this.showDeleteConfirm = false;
  }

  private _toggleSickStatus(status: SickStatus) {
    this.formSickStatus = this.formSickStatus === status ? null : status;
  }

  private _toggleExercise() {
    this.showExercise = !this.showExercise;
    if (!this.showExercise) {
      this.formExerciseDuration = "";
    }
  }

  private _getSickDayRecommendations() {
    if (!this.currentSickStatus && this.formSickStatus !== "sick") return null;

    // Calculate hours since sick started (approximate)
    const sickEntry = this.entries.find((e) => e.sickDay?.status === "sick");
    const hoursSick = sickEntry
      ? (new Date().getTime() - sickEntry.timestamp.getTime()) /
        (1000 * 60 * 60)
      : 0;

    const bg = this.formGL ? parseFloat(this.formGL) : undefined;
    const ket = this.formKet ? parseFloat(this.formKet) : undefined;

    return getSickDayRecommendation(bg, ket, hoursSick);
  }

  private _getExerciseRecommendations() {
    if (!this.showExercise || !this.formExerciseDuration) return null;

    const exercise: ExerciseData = {
      type: this.formExerciseType,
      intensity: this.formExerciseIntensity,
      duration: parseInt(this.formExerciseDuration),
    };

    return getExerciseRecommendation(exercise, "before", this.formPeriod);
  }

  private _getRecentExerciseRecommendations() {
    const recentExercise = getRecentExercise(this.entries);
    if (recentExercise.length === 0) return null;

    // Get the most recent exercise
    const latest = recentExercise[0];
    if (!latest.exercise) return null;

    return getExerciseRecommendation(latest.exercise, "after", this.formPeriod);
  }

  private _getGlClass(value?: number): string {
    if (!value) return "";
    if (value < 4) return "gl-hypo";
    if (value < 5) return "gl-low";
    if (value <= 7) return "gl-target";
    if (value <= 10) return "gl-elevated";
    return "gl-high";
  }

  private _getKetClass(value?: number): string {
    if (!value) return "";
    if (value < 0.6) return "ket-normal";
    if (value < 1.5) return "ket-elevated";
    return "ket-high";
  }

  private _getEntryCardClass(entry: LogEntry): string {
    const classes = ["entry-card"];
    if (entry.bloodGlucose && entry.bloodGlucose < 4) classes.push("has-hypo");
    else if (entry.ketones && entry.ketones >= 1.5)
      classes.push("has-high-ket");
    return classes.join(" ");
  }

  private _renderSickDayRecommendations() {
    const rec = this._getSickDayRecommendations();
    if (!rec) return "";

    const nextCheckTime = getNextCheckTime(new Date(), rec.nextBgCheckMinutes);

    return html`
      <div class="recommendations sick">
        <div class="rec-header">🤒 <strong>Sick Day Guidance</strong></div>
        ${rec.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
        ${rec.actions.map(
          (a) => html`
            <div class="rec-item action">
              <span class="icon">→</span>
              <span>${a}</span>
            </div>
          `,
        )}
        ${rec.checkKetones
          ? html`
              <div class="rec-item action">
                <span class="icon">🧪</span>
                <span
                  ><strong>Check ketones</strong> - important when sick</span
                >
              </div>
            `
          : ""}
        <div class="next-check">
          ⏰ <strong>Next BG check:</strong> ${formatNextCheckTime(
            rec.nextBgCheckMinutes,
          )}
          (at ${format(nextCheckTime, "HH:mm")})
        </div>
        ${rec.correctionMultiplier > 1
          ? html`
              <div class="rec-item action" style="margin-top: 0.5rem;">
                <span class="icon">💉</span>
                <span
                  >Consider ${Math.round((rec.correctionMultiplier - 1) * 100)}%
                  extra on correction doses</span
                >
              </div>
            `
          : ""}
      </div>
    `;
  }

  private _renderExerciseRecommendations() {
    const rec = this._getExerciseRecommendations();
    if (!rec) return "";

    return html`
      <div class="recommendations exercise">
        <div class="rec-header">
          🏃 <strong>Pre-Exercise Adjustment</strong>
        </div>
        <div class="rec-item action">
          <span class="icon">💉</span>
          <span
            >Reduce quick-acting insulin by
            <strong>${rec.qaReduction}%</strong></span
          >
        </div>
        <div class="rec-item action">
          <span class="icon">🍞</span>
          <span
            >OR add <strong>${rec.extraCarbs}g</strong> uncovered carbs</span
          >
        </div>
        <div class="rec-item" style="font-size: 0.75rem; color: #6c757d;">
          <span class="icon">ℹ️</span>
          <span>${rec.reason}</span>
        </div>
        ${rec.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _renderRecentExerciseWarning() {
    const rec = this._getRecentExerciseRecommendations();
    if (!rec) return "";

    const recentExercise = getRecentExercise(this.entries);
    if (recentExercise.length === 0) return "";

    const latest = recentExercise[0];
    const hoursAgo =
      Math.round(
        ((new Date().getTime() - latest.timestamp.getTime()) /
          (1000 * 60 * 60)) *
          10,
      ) / 10;

    return html`
      <div
        class="recommendations exercise"
        style="background: #e8f4f8; border-color: #17a2b8;"
      >
        <div class="rec-header">
          🏃 <strong>Recent Exercise Effect</strong>
          <span
            style="font-weight: normal; font-size: 0.75rem; color: #6c757d;"
          >
            (${hoursAgo}h ago)
          </span>
        </div>
        <div class="rec-item action">
          <span class="icon">💉</span>
          <span
            >Consider reducing correction by
            <strong>${rec.qaReduction}%</strong></span
          >
        </div>
        <div class="rec-item" style="font-size: 0.75rem; color: #6c757d;">
          <span class="icon">ℹ️</span>
          <span>${rec.reason}</span>
        </div>
        ${rec.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _getKetoneAlerts() {
    const ket = this.formKet ? parseFloat(this.formKet) : undefined;
    if (!ket) return null;

    const bg = this.formGL ? parseFloat(this.formGL) : undefined;
    return getKetoneAlert(ket, bg);
  }

  private _getBgKetoneWarning() {
    const bg = this.formGL ? parseFloat(this.formGL) : undefined;
    if (!bg) return null;

    const ket = this.formKet ? parseFloat(this.formKet) : undefined;
    return getBgKetoneAlert(bg, ket);
  }

  private _renderKetoneAlerts() {
    const alert = this._getKetoneAlerts();
    if (!alert || alert.level === "normal") return "";

    const cssClass =
      alert.level === "danger"
        ? "ketone-danger"
        : alert.level === "high"
          ? "ketone-high"
          : "ketone";

    const icon =
      alert.level === "danger" ? "🚨" : alert.level === "high" ? "⚠️" : "🧪";

    return html`
      <div class="recommendations ${cssClass}">
        <div class="rec-header">${icon} <strong>${alert.title}</strong></div>
        ${alert.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
        ${alert.actions.map(
          (a) => html`
            <div class="rec-item action">
              <span class="icon">→</span>
              <span>${a}</span>
            </div>
          `,
        )}
        ${alert.recheckMinutes > 0
          ? html`
              <div class="rec-item" style="margin-top: 0.5rem;">
                <span class="icon">⏰</span>
                <span
                  ><strong>Recheck ketones:</strong> ${formatNextCheckTime(
                    alert.recheckMinutes,
                  )}</span
                >
              </div>
            `
          : ""}
        ${alert.extraCorrectionPercent > 0
          ? html`
              <div class="rec-item action">
                <span class="icon">💉</span>
                <span
                  >Consider
                  <strong>${alert.extraCorrectionPercent}%</strong> extra on
                  correction dose</span
                >
              </div>
            `
          : ""}
      </div>
    `;
  }

  private _renderBgKetoneWarning() {
    const warning = this._getBgKetoneWarning();
    if (!warning || !warning.shouldCheckKetones) return "";

    return html`
      <div class="recommendations bg-warning">
        <div class="rec-header">
          🧪 <strong>Ketone Check Recommended</strong>
        </div>
        <div class="rec-item action">
          <span class="icon">→</span>
          <span>${warning.reason}</span>
        </div>
      </div>
    `;
  }

  private _renderEntry(entry: LogEntry) {
    const qaTotal = getQATotal(entry);
    const biTotal = entry.biBase !== undefined ? entry.biBase : undefined;

    return html`
      <div class=${this._getEntryCardClass(entry)}>
        <div class="entry-header">
          <span class="entry-time">${format(entry.timestamp, "HH:mm")}</span>
          <span class="entry-period"
            >${PERIOD_TAG_INFO[entry.periodTag].label}</span
          >
        </div>
        <div class="entry-data">
          ${entry.bloodGlucose
            ? html`
                <div class="entry-field">
                  <span class="label">GL</span>
                  <span class="value ${this._getGlClass(entry.bloodGlucose)}"
                    >${entry.bloodGlucose.toFixed(1)}</span
                  >
                </div>
              `
            : ""}
          ${entry.carbs
            ? html`
                <div class="entry-field">
                  <span class="label">CP</span>
                  <span class="value">${entry.carbs}g</span>
                </div>
              `
            : ""}
          ${qaTotal !== undefined
            ? html`
                <div class="entry-field">
                  <span class="label">Quick</span>
                  <span class="value"
                    >${formatInsulinDose(
                      entry.qaBase,
                      entry.qaCorrection,
                    )}u</span
                  >
                </div>
              `
            : ""}
          ${biTotal !== undefined
            ? html`
                <div class="entry-field">
                  <span class="label">Long</span>
                  <span class="value">${entry.biBase}u</span>
                </div>
              `
            : ""}
          ${entry.ketones !== undefined
            ? html`
                <div class="entry-field">
                  <span class="label">Ket</span>
                  <span class="value ${this._getKetClass(entry.ketones)}"
                    >${entry.ketones.toFixed(1)}</span
                  >
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <app-shell pageTitle=${this.editId ? "Edit Entry" : "Add Entry"}>
        <div class="page-header">
          <sl-button variant="text" @click=${this._cancel}>
            <sl-icon name="arrow-left"></sl-icon>
          </sl-button>
          <h2>${this.editId ? "Edit Entry" : "Add Entry"}</h2>
          <div style="display: flex; gap: 0.5rem;">
            ${this.editId
              ? html`
                  <sl-button
                    variant="danger"
                    size="small"
                    @click=${this._confirmDelete}
                  >
                    Delete
                  </sl-button>
                `
              : ""}
            <sl-button variant="primary" size="small" @click=${this._saveEntry}
              >Save</sl-button
            >
          </div>
        </div>

        ${this.showDeleteConfirm
          ? html`
              <sl-alert variant="danger" open style="margin: 1rem;">
                <strong>Delete this entry?</strong>
                <div style="margin-top: 0.5rem;">
                  <sl-button size="small" @click=${this._cancelDelete}
                    >Cancel</sl-button
                  >
                  <sl-button
                    variant="danger"
                    size="small"
                    @click=${this._deleteEntry}
                    >Delete</sl-button
                  >
                </div>
              </sl-alert>
            `
          : ""}

        <div class="page-content">
          ${this.activeIOB > 0
            ? html`
                <sl-alert
                  variant=${this.suggestedCorrection > 0 &&
                  this.activeIOB >= 0.5
                    ? "danger"
                    : "warning"}
                  open
                  class="stacking-alert"
                >
                  ${this.suggestedCorrection > 0 && this.activeIOB >= 0.5
                    ? html`
                        <strong>⚠️ Stacking Risk!</strong>
                        <div style="margin-top: 0.25rem;">
                          ${this.activeIOB}u still active from previous dose.
                          Adding correction now risks hypo.
                        </div>
                      `
                    : html`
                        <strong>Active IOB: ${this.activeIOB}u</strong> still
                        working from previous doses.
                      `}
                </sl-alert>
              `
            : ""}

          <!-- Status buttons -->
          <div class="status-buttons">
            <sl-button
              class="status-btn sick-btn ${this.formSickStatus === "sick"
                ? "active"
                : ""}"
              variant=${this.formSickStatus === "sick" ? "primary" : "default"}
              @click=${() => this._toggleSickStatus("sick")}
            >
              🤒 I'm Sick
            </sl-button>
            ${this.currentSickStatus
              ? html`
                  <sl-button
                    class="status-btn better-btn ${this.formSickStatus ===
                    "better"
                      ? "active"
                      : ""}"
                    variant=${this.formSickStatus === "better"
                      ? "primary"
                      : "default"}
                    @click=${() => this._toggleSickStatus("better")}
                  >
                    😊 I'm Better
                  </sl-button>
                `
              : ""}
            <sl-button
              class="status-btn exercise-btn ${this.showExercise
                ? "active"
                : ""}"
              variant=${this.showExercise ? "primary" : "default"}
              @click=${this._toggleExercise}
            >
              🏃 Exercise
            </sl-button>
          </div>

          <!-- Sick day recommendations -->
          ${this._renderSickDayRecommendations()}

          <!-- Exercise section -->
          ${this.showExercise
            ? html`
                <div class="exercise-section">
                  <div class="section-label">🏃 Exercise Details</div>
                  <div class="exercise-types">
                    ${(Object.keys(EXERCISE_TYPE_INFO) as ExerciseType[]).map(
                      (type) => html`
                        <sl-button
                          class="exercise-type-btn"
                          size="small"
                          variant=${this.formExerciseType === type
                            ? "primary"
                            : "default"}
                          @click=${() => (this.formExerciseType = type)}
                        >
                          ${EXERCISE_TYPE_INFO[type].label}
                        </sl-button>
                      `,
                    )}
                  </div>
                  <div class="intensity-row">
                    ${(
                      Object.keys(
                        EXERCISE_INTENSITY_INFO,
                      ) as ExerciseIntensity[]
                    ).map(
                      (intensity) => html`
                        <sl-button
                          size="small"
                          variant=${this.formExerciseIntensity === intensity
                            ? "primary"
                            : "default"}
                          @click=${() =>
                            (this.formExerciseIntensity = intensity)}
                        >
                          ${EXERCISE_INTENSITY_INFO[intensity].label}
                        </sl-button>
                      `,
                    )}
                  </div>
                  <sl-input
                    type="number"
                    min="5"
                    max="300"
                    step="5"
                    inputmode="numeric"
                    placeholder="Duration (minutes)"
                    .value=${this.formExerciseDuration}
                    @sl-input=${(e: Event) =>
                      (this.formExerciseDuration = (
                        e.target as HTMLInputElement
                      ).value)}
                  ></sl-input>
                </div>
              `
            : ""}

          <!-- Exercise recommendations -->
          ${this._renderExerciseRecommendations()}

          <!-- Recent exercise effect warning -->
          ${this._renderRecentExerciseWarning()}

          <div class="form-section">
            <div class="form-row-inline">
              <div>
                <label>Time</label>
                <sl-input
                  type="time"
                  .value=${this.formTime}
                  @sl-input=${(e: Event) =>
                    this._onTimeChange((e.target as HTMLInputElement).value)}
                ></sl-input>
              </div>
              <div>
                <label>Period</label>
                <sl-select
                  .value=${this.formPeriod}
                  @sl-change=${(e: Event) =>
                    this._onPeriodChange(
                      (e.target as HTMLSelectElement).value as PeriodTag,
                    )}
                >
                  <sl-option value="B">Breakfast</sl-option>
                  <sl-option value="after-B">After B</sl-option>
                  <sl-option value="L">Lunch</sl-option>
                  <sl-option value="after-L">After L</sl-option>
                  <sl-option value="EM">Evening</sl-option>
                  <sl-option value="after-EM">After EM</sl-option>
                  <sl-option value="BT">Bedtime</sl-option>
                  <sl-option value="3AM">3AM</sl-option>
                </sl-select>
              </div>
            </div>

            <div class="form-row-inline">
              <div>
                <label>GL <small>(mmol/L)</small></label>
                <sl-input
                  type="number"
                  step="0.1"
                  min="1"
                  max="33"
                  inputmode="decimal"
                  placeholder="e.g. 6.5"
                  .value=${this.formGL}
                  @sl-input=${(e: Event) =>
                    this._onGLChange((e.target as HTMLInputElement).value)}
                ></sl-input>
              </div>
              <div>
                <label
                  >Carbs <small>(g)</small>${this.calculatedCP > 0
                    ? html` <strong>= ${this.calculatedCP} CP</strong>`
                    : ""}</label
                >
                <sl-input
                  type="number"
                  step="1"
                  min="0"
                  max="500"
                  inputmode="numeric"
                  placeholder="e.g. 45"
                  .value=${this.formCP}
                  @sl-input=${(e: Event) =>
                    this._onCPChange((e.target as HTMLInputElement).value)}
                ></sl-input>
              </div>
            </div>

            ${this.correctionBlockedByIOB
              ? html`
                  <sl-alert variant="warning" open>
                    <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                    <strong>Active IOB: ${this.activeIOB}u</strong> — Correction
                    not suggested (DAFNE: no stacking). Would be
                    +${this.rawCorrection}u without IOB.
                  </sl-alert>
                `
              : this.activeIOB > 0 && this.suggestedCorrection > 0
                ? html`
                    <sl-alert variant="warning" open>
                      <sl-icon
                        slot="icon"
                        name="exclamation-triangle"
                      ></sl-icon>
                      <strong>Active IOB: ${this.activeIOB}u</strong> still
                      working from recent doses. Correction reduced from
                      +${this.rawCorrection}u to +${this.suggestedCorrection}u.
                    </sl-alert>
                  `
                : this.activeIOB > 0
                  ? html`
                      <sl-alert variant="neutral" open>
                        <sl-icon slot="icon" name="info-circle"></sl-icon>
                        <strong>Active IOB: ${this.activeIOB}u</strong> still
                        working from recent doses.
                      </sl-alert>
                    `
                  : ""}
            ${this.settingsMissing.length > 0
              ? html`
                  <sl-alert variant="warning" open>
                    <sl-icon slot="icon" name="gear"></sl-icon>
                    <strong>Setup Required</strong><br />
                    Configure your DAFNE settings to get insulin suggestions:
                    <ul style="margin: 0.5rem 0 0 1rem; padding: 0;">
                      ${this.settingsMissing.map((s) => html`<li>${s}</li>`)}
                    </ul>
                    <sl-button
                      size="small"
                      variant="primary"
                      style="margin-top: 0.5rem"
                      @click=${() => Router.go("/settings")}
                      >Go to Settings</sl-button
                    >
                  </sl-alert>
                `
              : ""}
            ${this.settingsMissing.length === 0 &&
            (this.suggestedQABase > 0 || this.suggestedCorrection !== 0)
              ? html`
                  <div class="correction-suggestion">
                    <div class="title">
                      ${this.suggestedQABase > 0 &&
                      this.suggestedCorrection !== 0
                        ? html`Suggested QA: ${this.suggestedQABase}u base
                            ${this.suggestedCorrection > 0 ? "+" : ""}${this
                              .suggestedCorrection}u
                            =
                            <strong
                              >${this.suggestedQABase +
                              this.suggestedCorrection}u
                              total</strong
                            >`
                        : this.suggestedQABase > 0
                          ? html`Suggested QA base: ${this.suggestedQABase}u`
                          : html`Suggested correction:
                            ${this.suggestedCorrection > 0 ? "+" : ""}${this
                              .suggestedCorrection}u`}
                      <sl-button size="small" @click=${this._applySuggestions}
                        >Apply</sl-button
                      >
                    </div>
                    <div class="detail">
                      ${this.suggestedQABase > 0
                        ? html`${this.calculatedCP} CP ×
                          ${this._getUnitsPerCP(
                            PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom ||
                              this.formPeriod,
                          )}u/CP
                          (${PERIOD_TAG_INFO[this.formPeriod]?.label ||
                          this.formPeriod})${this.suggestedCorrection !== 0
                            ? html`, `
                            : ""}`
                        : ""}
                      ${this.suggestedCorrection !== 0
                        ? html`GL ${this.formGL} → target
                          ${(
                            (this.settings.targetBgLow +
                              this.settings.targetBgHigh) /
                            2
                          ).toFixed(1)},
                          ISF
                          ${this.settings.correctionFactor}${this.activeIOB > 0
                            ? `, -${this.activeIOB}u IOB`
                            : ""}`
                        : ""}
                    </div>
                    ${IS_BETA
                      ? html`<div
                          class="beta-warning"
                          style="margin-top: 0.5rem; font-size: 0.75rem; color: #856404; background: #fff3cd; padding: 0.25rem 0.5rem; border-radius: 4px;"
                        >
                          ⚠️ ${BETA_WARNING.SHORT}
                        </div>`
                      : ""}
                  </div>
                `
              : ""}

            <div class="form-row">
              <label>Quick <small>(base + correction)</small></label>
              <div class="form-row-inline" style="margin-bottom: 0">
                <sl-input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  inputmode="decimal"
                  placeholder="Base (e.g. 4)"
                  .value=${this.formQABase}
                  @sl-input=${(e: Event) =>
                    (this.formQABase = (e.target as HTMLInputElement).value)}
                ></sl-input>
                <sl-input
                  type="number"
                  step="0.5"
                  min="-20"
                  max="20"
                  inputmode="decimal"
                  placeholder="+/- Corr"
                  .value=${this.formQACorr}
                  @sl-input=${(e: Event) =>
                    (this.formQACorr = (e.target as HTMLInputElement).value)}
                ></sl-input>
              </div>
            </div>

            <div class="form-row">
              <label>Long <small>(base + adjustment)</small></label>
              <div class="form-row-inline" style="margin-bottom: 0">
                <sl-input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  inputmode="decimal"
                  placeholder="Base (e.g. 14)"
                  .value=${this.formBIBase}
                  @sl-input=${(e: Event) =>
                    (this.formBIBase = (e.target as HTMLInputElement).value)}
                ></sl-input>
                <sl-input
                  type="number"
                  step="0.5"
                  min="-20"
                  max="20"
                  inputmode="decimal"
                  placeholder="+/- Adj"
                  .value=${this.formBICorr}
                  @sl-input=${(e: Event) =>
                    (this.formBICorr = (e.target as HTMLInputElement).value)}
                ></sl-input>
              </div>
            </div>

            <div class="form-row-inline">
              <div>
                <label>Ket <small>(mmol/L)</small></label>
                <sl-input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  inputmode="decimal"
                  placeholder="e.g. 0.3"
                  .value=${this.formKet}
                  @sl-input=${(e: Event) =>
                    (this.formKet = (e.target as HTMLInputElement).value)}
                ></sl-input>
              </div>
              <div></div>
            </div>

            <!-- Ketone alerts -->
            ${this._renderKetoneAlerts()}

            <!-- BG-based ketone check recommendation -->
            ${this._renderBgKetoneWarning()}

            <div class="form-row">
              <label>Note</label>
              <sl-textarea
                placeholder="Optional note..."
                rows="2"
                .value=${this.formNote}
                @sl-input=${(e: Event) =>
                  (this.formNote = (e.target as HTMLTextAreaElement).value)}
              ></sl-textarea>
            </div>

            <div class="form-actions">
              <sl-button variant="default" @click=${this._cancel}
                >Cancel</sl-button
              >
              <sl-button variant="primary" @click=${this._saveEntry}
                >Save Entry</sl-button
              >
            </div>
          </div>

          <!-- Today's entries -->
          <div class="entries-section">
            <div class="section-title">
              <sl-icon name="clock-history"></sl-icon>
              Today's Entries
            </div>
            ${this.entries.length === 0
              ? html`<p class="empty-entries">No entries yet today</p>`
              : this.entries.map((e) => this._renderEntry(e))}
          </div>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-entry-page": AddEntryPage;
  }
}
